//@flow
import { FormControl, FormLabel } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import { DataGridPremium, GridToolbar } from "@mui/x-data-grid-premium";
import axios from "axios";
import _ from "lodash";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import PageContentWrapper from "../../components/mui/app/pageContentWrapper/pageContentWrapper";
import PageTitle from "../../components/mui/app/pageTitle/pageTitle";
import CustomAutocomplete from "../../components/mui/form/autocomplete/autocomplete";
import DateRange from "../../components/mui/form/dateRange/dateRange";
import CollapseBox from "../../components/mui/general/collapseBox/collapseBox";
import SkeletonLoaderWrapper from "../../components/mui/general/skeletonLoaderWrapper/skeletonLoaderWrapper";
import { CustomPagination } from "../../components/mui/grids/dataGridCustom/customPagination";
import CustomToolbar from "../../components/mui/grids/dataGridCustom/customToolbar/customToolbar";
import DataGridWrapper from "../../components/mui/grids/dataGridWrapper/dataGridWrapper";
import { API } from "../../helpers/constants";
import { handleAxiosErrors, logoutUnauthorizedUser } from "../../helpers/errorHandling";
import { mapDateRange } from "../../helpers/helpers";
import { muiGridLocales } from "../../helpers/mui/muiLocales";
import {
	statistics_column_customer, statistics_column_fm,
	statistics_view_customer, statistics_view_fm,
	statistics_view_nmpi
} from "../../helpers/rights/rights";
import { formControlDefaults, numericFormatDigitDefaults } from "../../helpers/theme/defaultProps";
import useReportStaticData from "../../hooks/reports/useReportStaticData";
import useUserAction from "../../hooks/user/useUserAction";
import useUserData from "../../hooks/user/useUserData";
import { REPORTS } from "../../locales/pages/namespaces";
import PermissionHOC, { withPermissionHOC } from "../../modules/permission/components/permissionHOC/permissionHOC";
import { useHasPermission } from "../../modules/permission/hooks/useHasPermission";
import TruncateDescription from "../dashboard/partials/truncateDescription";
import {buildDynamicColumns} from "../../helpers/grid/getDynamicColumns";

type Props = {};

export const mapFilters = (filtersData) => {
	const {
		KindId,
		SubmitDt,
		Customer,
		Address,
		FM
	} = filtersData

	const [SubmitDtFrom, SubmitDtTo] = mapDateRange(SubmitDt)

	const getIds = (arr) =>
		Array.isArray(arr) && arr.length > 0
			? arr.map(i => i.id).join(',')
			: ''

	const filtersObject = {
		Kinds: getIds(KindId),
		SubmitDtFrom: SubmitDtFrom || '',
		SubmitDtTo: SubmitDtTo || '',
		Customer: getIds(Customer),
		FM: getIds(FM),
		Address: getIds(Address)
	}

	return { fields: filtersObject }
}
const Nmpi = (props: Props) => {
	const { t } = useTranslation(REPORTS)
	const { dateFormat, token, isLoggedIn, language, thousandSeparator, decimalSeparator } = useUserData()
	const { userLogout } = useUserAction()
	const [loadingSearch, setLoadingSearch] = useState(false)
	const [rows, setRows] = useState([])
	const [dynamicColumns, setDynamicColumns] = useState([])
	const [filtersData, setFiltersData] = useState({
		KindId: [],
		SubmitDt: [],
		Customer: [],
		Address: [],
		FM: []
	})
	const viewCustomerColumn = useHasPermission([statistics_column_customer], { shouldView: true })
	const viewFMColumn = useHasPermission([statistics_column_fm], { shouldView: true })
	const {
		loading,
		kinds,
		customers,
		addresses,
		FM
	} = useReportStaticData()
	const [customerOptions, setCustomerOptions] = useState(customers)
	const [sitesData, setSitesData] = useState(addresses)
	const [customerAddresses,setCustomerAddresses]= useState([])
	const [fmData, setFmData] = useState(FM)
	const [addressInput, setAddressInput] = useState('');

	useEffect(() => {
		setCustomerOptions(customers || []);
		setSitesData(addresses || [])
		setFmData(FM || [])
	}, [customers, addresses]);

	const handleSearch = (params, isClear = false) => {
		const { SubmitDtFrom, SubmitDtTo } = params.fields || {};
		if (!isClear && (!SubmitDtFrom || !SubmitDtTo)) {
			enqueueSnackbar(t('Η ημερομηνία είναι υποχρεωτική!'), {
				variant: 'warning',
				autoHideDuration: 3000,
			});
			return;
		}
		setLoadingSearch(true)
		axios({
			method: 'post',
			url: `${API}/en/custom/action`,
			data: {
				action_id: 'nmpi',
				fields: params.fields
			},
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
		}).then(res => {
			console.log('res', res.data)
			const data = res.data.data || []
			setRows(data)
			const monthCols = buildDynamicColumns(data, {
				includeKeys: ['tickets'],
				locale: language,
				thousandSeparator: thousandSeparator,
				decimalSeparator: decimalSeparator,
				label: 'Tickets / '
			})
			setDynamicColumns([...monthCols])
			setLoadingSearch(false)

			{
				data.length === 0 && !isClear &&
				enqueueSnackbar(t('No data found!'), {
					variant: 'warning',
					autoHideDuration: 3000,
				})
			}
		}).catch((error) => {
			setLoadingSearch(false)
			enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
				variant: 'error',
				autoHideDuration: 3000,
			})
			logoutUnauthorizedUser(error, isLoggedIn, userLogout)
		})
	}

	const columns = useMemo(() => [
		...(viewCustomerColumn ? [{
			field: 'Customer',
			headerName: t('Customer'),
			description: t('Customer'),
			flex: 1,
			minWidth: 150,
			renderCell: ({ row }) => {
				return <TruncateDescription value={row.Customer|| '-'}/>
			},
		}] : []),
		{
			field: 'Address',
			headerName: t('Address'),
			description: t('Address'),
			flex: 1,
			minWidth: 150,
			renderCell: ({ row }) => {
				return <TruncateDescription value={row.Address || '-'}/>
			},
		},
		...(viewFMColumn ? [{
			field: 'FM',
			headerName: t('FM'),
			description: t('FM'),
			flex: 1,
			minWidth: 150,
			renderCell: ({ row }) => {
				return <TruncateDescription value={row.FM || '-'}/>
			},
		}] : []),
		{
			field: 'Kind',
			headerName: t('Kind'),
			description: t('Kind'),
			flex: 1,
			minWidth: 150,
			renderCell: ({ row }) => row.Kind || '-'
		},
		{
			field: 'Type2',
			headerName: t('Type2'),
			description: t('Type2'),
			flex: 1,
			minWidth: 150,
			renderCell: ({ row }) => row.Type2 || '-'
		},
		// {
		// 	field: 'tickets',
		// 	headerName: t('Tickets'),
		// 	description: t('Tickets'),
		// 	flex: 1,
		// 	minWidth: 150,
		// 	type: 'number',
		// 	valueGetter: (value, row, column, apiRef) => value ? Number(value) : 0,
		// 	renderCell: (params) => {
		// 		const { value } = params
		// 		return (
		// 			<NumericFormat
		// 				{...numericFormatDigitDefaults}
		// 				thousandSeparator={thousandSeparator}
		// 				decimalSeparator={decimalSeparator}
		// 				displayType="text"
		// 				value={value}
		// 			/>
		// 		)
		// 	}
		// },
		...dynamicColumns
	], [t, viewCustomerColumn,dynamicColumns])
	const onChangeCustomer = useCallback((newValue) => {
		const getIds = (arr) =>
			Array.isArray(arr) && arr.length > 0
				? arr.map(i => i.id).join(',')
				: ''
		const customerIds = getIds(newValue)
		setLoadingSearch(true)
		axios({
			method: 'post',
			data: {
				CustId: customerIds,
				ReturnData: ['addresses', 'FM']
			},
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
			url: `${API}/en/issues/issueCustData`
		}).then((res) => {
			setLoadingSearch(false)
			setSitesData(_.orderBy(res.data?.addresses?.map(i => ({
				id: i.id,
				Description: i.Site
			})), ['Description'], ['asc']))

			setCustomerAddresses(_.orderBy(res.data?.addresses?.map(i => ({
				id: i.id,
				Description: i.Site
			})), ['Description'], ['asc']))
			setFmData(_.orderBy(res.data?.FM?.map(i => ({
				id: i.id,
				Description: i.Description
			})), ['Description'], ['asc']))
			console.log('res.data', res.data)
		}).catch((error) => {
			setLoadingSearch(false)
			enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
				variant: 'error',
				autoHideDuration: 3000,
			})
			logoutUnauthorizedUser(error, isLoggedIn, userLogout)
		})
	}, [t, isLoggedIn, userLogout])

	const searchCustomers = useCallback((evt) => {
		setLoadingSearch(true)
		axios({
			method: 'post',
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
			data: {
				query: evt.target.value,
			},
			url: `${API}/en/customers/search`
		}).then((response) => {
			setLoadingSearch(false)
			setCustomerOptions(response.data.data)
		}).catch((error) => {
			enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
				variant: 'error',
				autoHideDuration: 3000,
			})
			setLoadingSearch(false)
			logoutUnauthorizedUser(error, isLoggedIn, userLogout)
		})
	}, [t, isLoggedIn, userLogout])

	const searchSites = useCallback((query='') => {
		const getIds = (arr) =>
			Array.isArray(arr) && arr.length > 0
				? arr.map(i => i.id).join(',')
				: ''
		const customerIds = getIds(filtersData.Customer)
		setLoadingSearch(true)
		axios({
			method: 'post',
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
			data: {
				CustId:customerIds,
				query:query ,
				...(customerIds && { CustId: customerIds }),
				Type: 'Site'
			},
			url: `${API}/en/customer/address/search`
		}).then((response) => {
			setSitesData(response.data.data)
			setLoadingSearch(false)
		}).catch((error) => {
			setLoadingSearch(false)
			enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
				variant: 'error',
				autoHideDuration: 3000,
			})
			logoutUnauthorizedUser(error, isLoggedIn, userLogout)
		})
	}, [filtersData.Customer, t, isLoggedIn, userLogout])

	return (
		<Box>
			<PageContentWrapper>
				<PageTitle title={t('NMPI')}/>
				<SkeletonLoaderWrapper loading={loading}>
					<CollapseBox title={t('Filter Options')}>
						<Grid container spacing={2} sx={{ mt: 1, alignItems: 'flex-end' }}>
							<PermissionHOC requiredPermissions={[statistics_view_customer]}>
								<Grid size={{ xs: 6, sm: 4, md: 4, lg: 4 }}>
									<FormControl
										{...formControlDefaults}
									>
										<FormLabel>{t('Customer')}</FormLabel>
										<CustomAutocomplete
											identifier="id"
											name="Customer"
											onChange={(evt, newValue, reason) => {
												setFiltersData({
													...filtersData,
													Customer: newValue
												})
												if (reason === 'clear') {
													setCustomerOptions([...customers])
													setSitesData([...addresses])
												} else {
													onChangeCustomer(newValue)
												}
											}}
											value={filtersData.Customer}
											options={customerOptions}
											labelKey={'Description'}  //LoginName or AssigneeName
											placeholder={t('Select one or more..')}
											onInputChange={(evt) => searchCustomers(evt)}
											otherprops={{
												multiple: true,
												disableCloseOnSelect: true,
												limitTags: 1,
												getOptionKey: (option) => (option?.id)
											}}
										/>
									</FormControl>
								</Grid>
							</PermissionHOC>
							<Grid size={{ xs: 6, sm: 4, md: 4, lg: 4 }}>
								<FormControl
									{...formControlDefaults}
								>
									<FormLabel>{t('Site')}</FormLabel>
									<CustomAutocomplete
										identifier="id"
										name="Address"
										onChange={(evt, newValue, reason) => {
											setFiltersData({
												...filtersData,
												Address: newValue
											})
											if (reason === 'clear' && !filtersData.Customer?.id) {
												setSitesData([...addresses])
												setAddressInput('');
											}
										}}
										value={filtersData.Address}
										options={sitesData}
										labelKey={'Description'}  //LoginName or AssigneeName
										placeholder={t('Select one or more..')}
										otherprops={{
											multiple: true,
											disableCloseOnSelect: true,
											limitTags: 1,
											getOptionKey: (option) => (option?.id),
											inputValue:addressInput,
											onInputChange: (event, newValue) => {
												setAddressInput(newValue ?? '');
												searchSites(newValue);
											},
											onClose: (event, reason) => {
												if (['blur', 'escape', 'toggleInput'].includes(reason)) {
													setSitesData(customerAddresses || []);
													setAddressInput('');
												}
											},
										}}
									/>
								</FormControl>
							</Grid>
							<Grid size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
								<FormControl {...formControlDefaults}>
									<FormLabel>{t('Submit Date')}</FormLabel>
									<DateRange
										dateFormat={dateFormat}
										value={filtersData.SubmitDt}
										onChange={(newValue) => {
											setFiltersData(prev => ({
												...prev,
												SubmitDt: newValue
											}))
										}}
										onClear={() => (
											setFiltersData(prev => ({
												...prev,
												SubmitDt: null
											}))
										)}
									/>
								</FormControl>
							</Grid>
							<PermissionHOC requiredPermissions={[statistics_view_fm]}>
								<Grid size={{ xs: 6, sm: 4, md: 3, lg: 2 }}>
									<FormControl
										{...formControlDefaults}
									>
										<FormLabel>{t('FM')}</FormLabel>
										<CustomAutocomplete
											identifier="id"
											name="FM"
											onChange={(evt, newValue, reason) => {
												setFiltersData({
													...filtersData,
													FM: newValue
												})
												if (reason === 'clear' ) {
													setFmData([...FM])
												}
											}}
											value={filtersData.FM}
											options={fmData}
											labelKey={'Description'}
											placeholder={t('Select one or more..')}
											otherprops={{
												multiple: true,
												disableCloseOnSelect: true,
												limitTags: 1,
												getOptionKey: (option) => (option?.id)
											}}
										/>
									</FormControl>
								</Grid>
							</PermissionHOC>
							<Grid size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
								<FormControl
									{...formControlDefaults}
								>
									<FormLabel>{t('Kind')}</FormLabel>
									<CustomAutocomplete
										identifier="id"
										name="Kind"
										onChange={(evt, newValue, reason) => {
											setFiltersData({
												...filtersData,
												KindId: newValue
											})
										}}
										value={filtersData.KindId}
										options={kinds}
										labelKey={'Description'}  //LoginName or AssigneeName
										placeholder={t('Select one or more..')}
										otherprops={{
											multiple: true,
											disableCloseOnSelect: true,
											limitTags: 1,
											getOptionKey: (option) => (option?.id)
										}}
									/>
								</FormControl>
							</Grid>
							<Grid size={{ xs: 6, sm: 6, md: 6, lg: 4 }} sx={{ p: 0.4 }}>
								<Box display={'flex'} alignItems={'flex-end'} justifyContent={'center'}
									 flexWrap={'wrap'} gap={1}>
									<Button
										loading={loadingSearch}
										onClick={() => {
											const params = {
												...mapFilters(filtersData)
											}
											handleSearch(params, false)
										}}
										variant="outlined"
										color={'success'}
									>
										{t('Search')}
									</Button>
									<Button
										loading={loadingSearch}
										onClick={() => {
											const clearedFilters = {
												KindId: [],
												SubmitDt: null,
												Customer: [],
												Address: [],
												FM: []
											}
											setFiltersData(clearedFilters)

											const params = mapFilters(clearedFilters)
											handleSearch(params, true)
										}}
										variant="outlined"
										color={'error'}
									>
										{t('Clear')}
									</Button>
								</Box>
							</Grid>
						</Grid>
					</CollapseBox>
				</SkeletonLoaderWrapper>
				{rows.length > 0 &&
					<DataGridWrapper sx={{ height: 700 }}>
						<DataGridPremium
							rows={rows}
							columns={columns}
							loading={loadingSearch}
                            getRowId={(row)=>row.id}
							pagination
							pageSize={100}
							rowsPerPageOptions={[10, 20, 50, 100]}
							initialState={{
								density: 'compact'
							}}
							slots={{
								toolbar: GridToolbar,
								pagination: CustomPagination,
							}}
							disableSelectionOnClick
							localeText={muiGridLocales(language)}
						/>

					</DataGridWrapper>}
			</PageContentWrapper>
		</Box>
	);
}

export default withPermissionHOC(Nmpi, [statistics_view_nmpi])